import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  IconButton,
  Collapse
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(14),
    "&.success": {
      backgroundColor: "#d4edda",
      color: "#155724",
      border: "1px solid #c3e6cb",
      "& .MuiSvgIcon-root": {
        color: "#28a745"
      }
    },
    "&.error": {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      border: "1px solid #f5c6cb",
      "& .MuiSvgIcon-root": {
        color: "#dc3545"
      }
    },
    "&.warning": {
      backgroundColor: "#fff3cd",
      color: "#856404",
      border: "1px solid #ffeaa7",
      "& .MuiSvgIcon-root": {
        color: "#ffc107"
      }
    },
    "&.info": {
      backgroundColor: "#d1ecf1",
      color: "#0c5460",
      border: "1px solid #bee5eb",
      "& .MuiSvgIcon-root": {
        color: "#17a2b8"
      }
    }
  },
  icon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem"
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },
  title: {
    fontWeight: 500,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5)
  },
  message: {
    lineHeight: 1.4
  },
  closeButton: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
    "& .MuiSvgIcon-root": {
      fontSize: "1rem"
    }
  },
  filled: {
    color: "#fff",
    "&.success": {
      backgroundColor: "#28a745",
      border: "1px solid #28a745"
    },
    "&.error": {
      backgroundColor: "#dc3545",
      border: "1px solid #dc3545"
    },
    "&.warning": {
      backgroundColor: "#ffc107",
      color: "#212529",
      border: "1px solid #ffc107"
    },
    "&.info": {
      backgroundColor: "#17a2b8",
      border: "1px solid #17a2b8"
    }
  }
}));

export const Alert = ({
  severity = "info",
  variant = "standard",
  title,
  children,
  icon,
  onClose,
  className = "",
  ...props
}) => {
  const classes = useStyles();

  const getIcon = () => {
    if (icon !== undefined) {
      return icon;
    }

    switch (severity) {
      case "success":
        return <CheckCircleIcon />;
      case "error":
        return <ErrorIcon />;
      case "warning":
        return <WarningIcon />;
      case "info":
      default:
        return <InfoIcon />;
    }
  };

  const rootClasses = [
    classes.root,
    severity,
    variant === "filled" ? classes.filled : "",
    className
  ].filter(Boolean).join(" ");

  return (
    <Box className={rootClasses} {...props}>
      {getIcon() && (
        <Box className={classes.icon}>
          {getIcon()}
        </Box>
      )}

      <Box className={classes.content}>
        {title && (
          <Typography className={classes.title} component="div">
            {title}
          </Typography>
        )}

        {children && (
          <Typography className={classes.message} component="div">
            {children}
          </Typography>
        )}
      </Box>

      {onClose && (
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default Alert;